import Visibility from "@mapmycustomers/shared/enum/Visibility";
import { EntityType, EntityTypeSupportingPinnedFields } from "@mapmycustomers/shared/types/entity";
import SourceObjectValue from "@mapmycustomers/shared/types/SourceObjectValue";

import { SourceObjectEntityType } from "../constants";
import EntityColumnsConfiguration, { SubtypeList } from "../EntityColumnsConfiguration";

import combinePinnedList, { PinnedLevelSources } from "./combinePinnedList";
import { ResolveField } from "./convertToFieldDescription";
import getPinnedSubtypeKeys from "./getPinnedSubtypeKeys";

interface BuildEntityColumnsConfigurationInput extends PinnedLevelSources {
  isOwner: boolean;
  resolveField: ResolveField;
  sourceObjectValues: Record<SourceObjectEntityType, SourceObjectValue[]>;
}

// Builds the pinned lists every level stores into the one configuration the app shows and edits
const buildEntityColumnsConfiguration = ({
  isOwner,
  org,
  resolveField,
  sourceObjectValues,
  teams,
  user,
}: BuildEntityColumnsConfigurationInput): EntityColumnsConfiguration => {
  const orgMetadataMapConfiguration = org ?? {};
  const userMetadataMapConfiguration = user ?? {};
  const teamsMetadataMapConfiguration = teams.map(
    ({ metaData }) => metaData?.mapConfiguration ?? {}
  );

  const quickActionsVisibility =
    (orgMetadataMapConfiguration?.fields?.actions ??
    orgMetadataMapConfiguration?.actions?.[EntityType.COMPANY])
      ? Visibility.SHARED_WITH_ORGANIZATION
      : teamsMetadataMapConfiguration.some(
            (config) => config.fields?.actions ?? config.actions?.[EntityType.COMPANY]
          )
        ? Visibility.SHARED_WITH_TEAM
        : (userMetadataMapConfiguration?.fields?.actions ??
            userMetadataMapConfiguration?.actions?.[EntityType.COMPANY])
          ? Visibility.PRIVATE
          : undefined;

  const quickActionsTeamIds =
    quickActionsVisibility === Visibility.SHARED_WITH_TEAM
      ? teams
          .filter(
            ({ metaData }) =>
              metaData?.mapConfiguration?.fields?.actions ??
              metaData?.mapConfiguration?.actions?.[EntityType.COMPANY]
          )
          .map(({ id }) => id)
      : undefined;

  const engagementVisibility =
    (orgMetadataMapConfiguration?.fields?.engagement ??
    orgMetadataMapConfiguration?.engagement?.[EntityType.COMPANY])
      ? Visibility.SHARED_WITH_ORGANIZATION
      : teamsMetadataMapConfiguration.some(
            (config) => config.fields?.engagement ?? config.engagement?.[EntityType.COMPANY]
          )
        ? Visibility.SHARED_WITH_TEAM
        : (userMetadataMapConfiguration?.fields?.engagement ??
            userMetadataMapConfiguration?.engagement?.[EntityType.COMPANY])
          ? Visibility.PRIVATE
          : undefined;

  const engagementTeamIds =
    engagementVisibility === Visibility.SHARED_WITH_TEAM
      ? teams
          .filter(
            ({ metaData }) =>
              metaData?.mapConfiguration?.fields?.engagement ??
              metaData?.mapConfiguration?.engagement?.[EntityType.COMPANY]
          )
          .map(({ id }) => id)
      : undefined;

  const sources = { org, teams, user };
  const options = { isOwner, resolveField };
  const getSharedList = (entityType: EntityTypeSupportingPinnedFields) =>
    combinePinnedList({ entityType, kind: "shared" }, sources, options);
  const getSubtypeLists = (entityType: SourceObjectEntityType): SubtypeList[] =>
    getPinnedSubtypeKeys(sourceObjectValues[entityType]).map((key) => ({
      fields: combinePinnedList({ entityType, key, kind: "subtype" }, sources, options),
      key,
    }));

  return {
    actions: {
      teamIds: quickActionsTeamIds,
      visibility: quickActionsVisibility,
    },
    engagement: {
      teamIds: engagementTeamIds,
      visibility: engagementVisibility,
    },
    [EntityType.ACTIVITY]: getSharedList(EntityType.ACTIVITY),
    [EntityType.COMPANY]: getSharedList(EntityType.COMPANY),
    [EntityType.DEAL]: getSharedList(EntityType.DEAL),
    [EntityType.PERSON]: getSharedList(EntityType.PERSON),
    // Every record type, even one without subtypes, so the store replaces stale lists whole
    subtypeLists: {
      [EntityType.COMPANY]: getSubtypeLists(EntityType.COMPANY),
      [EntityType.PERSON]: getSubtypeLists(EntityType.PERSON),
    },
  };
};

export default buildEntityColumnsConfiguration;
